import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Paper,
} from '@mui/material';

interface SqlResultsTableProps {
  results: Record<string, unknown>[];
  compact?: boolean;
}

type SortDirection = 'asc' | 'desc';

const SqlResultsTable: React.FC<SqlResultsTableProps> = ({ results, compact = false }) => {
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  if (!results.length) return null;

  const columns = Object.keys(results[0]);
  const maxRows = compact ? 5 : results.length;

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedResults = [...results].sort((a, b) => {
    if (!sortColumn) return 0;
    const aVal = a[sortColumn];
    const bVal = b[sortColumn];
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    }
    const aStr = String(aVal);
    const bStr = String(bVal);
    return sortDirection === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
  });

  const displayedResults = sortedResults.slice(0, maxRows);

  const formatCell = (value: unknown): string => {
    if (value == null) return '-';
    if (typeof value === 'number') {
      return Number.isInteger(value) ? value.toLocaleString() : value.toFixed(2);
    }
    return String(value);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Query Results
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: compact ? 200 : 400 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col} sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                  <TableSortLabel
                    active={sortColumn === col}
                    direction={sortColumn === col ? sortDirection : 'asc'}
                    onClick={() => handleSort(col)}
                  >
                    {col.replace(/_/g, ' ')}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedResults.map((row, i) => (
              <TableRow key={i} hover>
                {columns.map((col) => (
                  <TableCell key={col} sx={{ fontSize: '0.75rem' }}>
                    {formatCell(row[col])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {results.length > maxRows && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Showing {maxRows} of {results.length} rows
        </Typography>
      )}
    </Box>
  );
};

export default SqlResultsTable;
